import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { Label, Input, FormGroup, Button, Row, Col } from 'reactstrap';
import Select from 'react-select';

import {
    IPlayersVm,
    IPlayerDto,
    PlayersClient,
    UpdatePlayerCommand,
    IUsersVm,
    UsersClient,
    IUserDto
} from '../../../WorldDoomLeague';
import { ErrorContext } from '../../../state';
import AssociatePlayer from './AssociatePlayer';
import DisassociatePlayer from './DisassociatePlayer';
import AddPlayer from './AddPlayer';

const EditPlayers = (props) => {
    const error = useContext(ErrorContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [playerDataChanged, setPlayerDataChanged] = useState<boolean>(false);
    const [playerChangedName, setPlayerChangedName] = useState<string>("");
    const [playerFormName, setPlayerFormName] = useState<string>("");
    const [playerFormAlias, setPlayerFormAlias] = useState<string>("");
    const [editedPlayerId, setEditedPlayerId] = useState(0);
    const [newPlayerId, setNewPlayerId] = useState(0);
    const [editedUserId, setEditedUserId] = useState<string>("");
    const [player, setPlayer] = useState(0);

    const [data, setData] = useState<IPlayerDto[]>([]);
    const [userData, setUserData] = useState<IUserDto[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let client = new PlayersClient();
                const response = await client.get()
                    .then(response => response.toJSON() as Promise<IPlayersVm>);
                const data = response.playerList;
                setData(data);
            } catch (e) {
                error.setError(JSON.parse(e.response));
            }
            setLoading(false);
        };

        fetchData();
    }, [editedPlayerId, newPlayerId]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let client = new UsersClient();
                const response = await client.get()
                    .then(response => response.toJSON() as Promise<IUsersVm>);
                const data = response.userList;
                setUserData(data);
            } catch (e) {
                error.setError(JSON.parse(e.response));
            }
            setLoading(false);
        };

        fetchData();
    }, [editedUserId]);

    const checkIfPlayerDataChanged = (playerName: string, playerAlias: string) => {
        if (playerName == data.find(o => o.id == player).playerName && playerAlias == data.find(o => o.id == player).playerAlias) {
            setPlayerDataChanged(false);
        } else {
            setPlayerDataChanged(true);
        }
    };

    const handleFormPlayerNameChanged = (playerName: string) => {
        checkIfPlayerDataChanged(playerName, playerFormAlias);

        setPlayerFormName(playerName);
    };

    const handleFormPlayerAliasChanged = (playerAlias: string) => {
        checkIfPlayerDataChanged(playerFormName, playerAlias);

        setPlayerFormAlias(playerAlias);
    };

    const handlePlayerListSelected = (playerId: number) => {
        setPlayerFormName(data.find(o => o.id == playerId).playerName);
        setPlayerFormAlias(data.find(o => o.id == playerId).playerAlias);
        setPlayer(playerId);
    };

    const editPlayer = async (evt) => {
        try {
            let client = new PlayersClient();
            const command = new UpdatePlayerCommand;
            command.playerId = player;
            command.playerName = playerFormName;
            command.playerAlias = playerFormAlias;
            const response = await client.update(player, command);
            setPlayer(0);
            setEditedPlayerId(response);
            setPlayerChangedName(playerFormName);
            setPlayerFormName('');
            setPlayerFormAlias('');
            setPlayerDataChanged(false);
        } catch (e) {
            error.setError(JSON.parse(e.response));
        }
    };

    // create a list for each engine.
    const renderPlayerDropdown = () => {
        let select = null;
        if (data.length > 0) {
            select = (
                <Select
                    options={data}
                    onChange={e => handlePlayerListSelected(e.id)}
                    isSearchable={true}
                    value={data.find(o => o.id == player) || null}
                    getOptionValue={value => value.id.toString()}
                    getOptionLabel={label => `${label.playerName}`}
                    isLoading={loading}
                    theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'dimgrey',
                            neutral0: 'black',
                            neutral80: 'white'
                        },
                    })}
                    className="mb-3"
                />)
        } else {
            select = (<Select options={[{ label: "No players found in the system.", value: "Not" }]} theme={theme => ({
                ...theme,
                borderRadius: 0,
                colors: {
                    ...theme.colors,
                    primary25: 'dimgrey',
                    neutral0: 'black',
                    neutral80: 'white'
                },
            })}/>);
        }
        return (select);
    };

    // create a form for editing a player.
    const renderEditPlayerForm = () => {
        return (
            <React.Fragment>
                <FormGroup>
                    <Label for='playername'>Player Name</Label>
                    <Input type='text' className='form-control' id='playername' name='playername' placeholder='Player Name' value={playerFormName} disabled={!(player > 0)} onChange={e => handleFormPlayerNameChanged(e.target.value)} />
                </FormGroup>
                <FormGroup>
                    <Label for='playeralias'>Player Alias</Label>
                    <Input type='text' className='form-control' id='playeralias' name='playeralias' placeholder='Player Alias' value={playerFormAlias} disabled={!(player > 0)} onChange={e => handleFormPlayerAliasChanged(e.target.value)} />
                </FormGroup>
                <Button color="primary" size="lg" block disabled={!playerDataChanged} onClick={editPlayer}>Edit Player</Button>
            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            <Row className='mb-3'>
                <Col>
                    <h3 className='text-center'>Edit Player</h3>
                    <p>Please select a player to make changes to.</p>
                    {renderPlayerDropdown()}
                    {renderEditPlayerForm()}
                    {playerChangedName !== "" && (<h3 className='text-center'>{playerChangedName} has been successfully changed!</h3>)}
                </Col>
                <Col>
                    <AssociatePlayer setEditedUserId={setEditedUserId} data={userData} playerId={player} />
                </Col>
                <Col>
                    <DisassociatePlayer setEditedUserId={setEditedUserId} data={userData} />
                </Col>
            </Row>
            <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <AddPlayer setNewPlayerId={setNewPlayerId} />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default EditPlayers